import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import es from 'date-fns/locale/es';
import DatePicker, { registerLocale } from 'react-datepicker';
import { Button, Form, Modal } from 'react-bootstrap';
import 'react-datepicker/dist/react-datepicker.css';

import './Dates.css';

import { selectFilters, updateFilters } from './layoutSlice';
import {
    formatFilterSummary,
    getMinYear,
    getMaxYear,
    isValidFilterDraft,
    normalizeLegacyFilters,
    parseYear,
    YEAR_LOOKBACK,
} from '../../utils/calcularFechas';

registerLocale('es', es);

const MONTHS = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' },
];

const FILTER_OPTIONS = [
    { value: 'rango', label: 'Rango' },
    { value: 'mayor', label: 'Mayor a' },
    { value: 'mes', label: 'Mes y Año' },
    { value: 'anno', label: 'Año' },
];

const MAX_DATE = new Date();
const MIN_DATE = moment().subtract(YEAR_LOOKBACK, 'years').startOf('year').toDate();

const DATE_PICKER_PROPS = {
    locale: 'es',
    dateFormat: 'dd/MM/yyyy',
    showMonthDropdown: true,
    showYearDropdown: true,
    dropdownMode: 'select',
    scrollableYearDropdown: true,
    yearDropdownItemNumber: YEAR_LOOKBACK,
    minDate: MIN_DATE,
    maxDate: MAX_DATE,
    popperPlacement: 'bottom-start',
};

const toDate = (timestamp) => moment(timestamp).toDate();

const FilterDatePicker = ({ selected, onChange, disabled }) => (
    <DatePicker
        selected={selected}
        onChange={onChange}
        disabled={disabled}
        calendarClassName="dashboard-datepicker"
        popperClassName="dashboard-datepicker-popper"
        {...DATE_PICKER_PROPS}
    />
);

const Dates = () => {
    const filters = useSelector(selectFilters);
    const dispatch = useDispatch();
    const [showModal, setShowModal] = useState(false);
    const [draft, setDraft] = useState(filters);

    const openModal = () => {
        setDraft(normalizeLegacyFilters(filters));
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
    };

    const canApply = isValidFilterDraft(draft);

    const applyFilters = () => {
        if (!canApply) {
            return;
        }

        dispatch(updateFilters(draft));
        setShowModal(false);
    };

    const updateDraft = (changes) => {
        setDraft((prev) => ({ ...prev, ...changes }));
    };

    const isActive = (type) => draft.filterType === type;

    return (
        <>
            <div className="pb-3 dates-container">
                <span className="dates-summary me-3">{formatFilterSummary(filters)}</span>
                <Button variant="outline-primary" size="sm" onClick={openModal}>
                    <i className="fa-regular fa-calendar me-2"></i>
                    Filtros
                </Button>
            </div>

            <Modal show={showModal} onHide={closeModal} size="sm" centered className="filters-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Opciones por fecha</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {FILTER_OPTIONS.map(({ value, label }) => (
                        <div key={value} className="filter-row">
                            <Form.Check
                                type="radio"
                                id={`filter-${value}`}
                                name="filterType"
                                label={label}
                                checked={isActive(value)}
                                onChange={() => updateDraft({ filterType: value })}
                                className="filter-radio"
                            />
                            <div className={`filter-inputs ${isActive(value) ? '' : 'filter-inputs--disabled'}`}>
                                {value === 'rango' && (
                                    <>
                                        <FilterDatePicker
                                            selected={toDate(draft.fechaDesde)}
                                            onChange={(date) =>
                                                updateDraft({ fechaDesde: moment(date).valueOf() })
                                            }
                                            disabled={!isActive('rango')}
                                        />
                                        <FilterDatePicker
                                            selected={toDate(draft.fechaHasta)}
                                            onChange={(date) =>
                                                updateDraft({ fechaHasta: moment(date).valueOf() })
                                            }
                                            disabled={!isActive('rango')}
                                        />
                                    </>
                                )}
                                {value === 'mayor' && (
                                    <FilterDatePicker
                                        selected={toDate(draft.fechaMayor)}
                                        onChange={(date) =>
                                            updateDraft({ fechaMayor: moment(date).valueOf() })
                                        }
                                        disabled={!isActive('mayor')}
                                    />
                                )}
                                {value === 'mes' && (
                                    <>
                                        <Form.Select
                                            size="sm"
                                            value={draft.mes}
                                            onChange={(e) =>
                                                updateDraft({ mes: Number(e.target.value) })
                                            }
                                            disabled={!isActive('mes')}
                                        >
                                            {MONTHS.map(({ value: monthValue, label: monthLabel }) => (
                                                <option key={monthValue} value={monthValue}>
                                                    {monthLabel}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Control
                                            type="number"
                                            size="sm"
                                            value={draft.annoMes}
                                            min={getMinYear()}
                                            max={getMaxYear()}
                                            onChange={(e) =>
                                                updateDraft({
                                                    annoMes: parseYear(e.target.value, draft.annoMes),
                                                })
                                            }
                                            disabled={!isActive('mes')}
                                        />
                                    </>
                                )}
                                {value === 'anno' && (
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        value={draft.anno}
                                        min={getMinYear()}
                                        max={getMaxYear()}
                                        onChange={(e) =>
                                            updateDraft({
                                                anno: parseYear(e.target.value, draft.anno),
                                            })
                                        }
                                        disabled={!isActive('anno')}
                                    />
                                )}
                            </div>
                        </div>
                    ))}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={applyFilters} disabled={!canApply}>
                        Aplicar
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default Dates;
